import { COLORS, useMediaQuery } from '@sortlist-frontend/design-system/legacy';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Dispatch, FC, Fragment, SetStateAction, useEffect, useMemo, useState } from 'react';

import { AgencySearchBlock } from '_components/common/AgencySearchBlock';
import type { PressSectionProps } from '_components/common/PressSection';
import { SectionShape } from '_components/common/SectionShape';
import { StoryCard } from '_components/common/StoryCard';
import { PageSeoTag } from '_components/SEO';
import { usePublicAppContext } from '_core/context/public-app-context';
import { PublicApiRepo, useGetLinks, useGetPage } from '_core/repos/public-api.repo';
import { customConfig } from '_features/custom/custom.config';
import { HeroHeader } from '_features/stories/components/header/header.component';
import { StoriesSorter } from '_features/stories/components/stories-sorter/stories-sorter.component';
import * as S from '_features/stories/stories.styles';
import { BusinessOptions, filteredStoriesLinks, storiesType, StoriesTypeProp } from '_features/stories/utils';

const PressSection = dynamic<PressSectionProps>(
  () => import('_components/common/PressSection').then((mod) => mod.PressSection),
  {
    ssr: false,
  },
);

const StoriesPage = () => {
  const { t } = useTranslation(customConfig.i18nNamespaces);
  const { canonical } = usePublicAppContext();
  const { data: links } = useGetLinks(PublicApiRepo.publicApiUrl);
  const { data: page } = useGetPage(PublicApiRepo.publicApiUrl);

  const { media_legacy } = useMediaQuery();

  const { query } = useRouter();
  const attributes = page?.data.attributes;

  const defaultAllValue = { key: 'all', label: t('stories:sorter.allSorter') as string };
  const [selectedType, setSelectedType] = useState(defaultAllValue);
  const [selectedCountry, setSelectedCountry] = useState(defaultAllValue);

  const newStory = filteredStoriesLinks(links, 'stories')?.[0]; // stories with position 0 we'll be the latest;
  const stories = !media_legacy.gtSm
    ? filteredStoriesLinks(links, 'stories')
    : filteredStoriesLinks(links, 'stories')?.slice(1);

  // Populate options from db
  const optionsType = useMemo(
    () => stories && storiesType(stories, t, BusinessOptions.CATEGORY, query),
    [query, stories, t],
  );

  const optionCountry = useMemo(
    () => stories && storiesType(stories, t, BusinessOptions.COUNTRY, query),
    [query, stories, t],
  );

  const sortedStories = useMemo(
    () =>
      stories &&
      stories
        .filter((story) =>
          selectedType.key === 'all' ? stories : story?.metadata?.business?.type === selectedType.key,
        )
        .filter((story) =>
          selectedCountry.key === 'all' ? stories : story?.metadata?.business?.iso31661 === selectedCountry.key,
        ),
    [selectedCountry, selectedType, stories],
  );

  useEffect(() => {
    query.category && setSelectedType({ key: query.category as string, label: query.category as string });
    query.country && setSelectedCountry({ key: query.country as string, label: query.country as string });
  }, [query]);

  return (
    <Fragment>
      {attributes && (
        <PageSeoTag
          title={attributes.metadata.title_tag}
          description={attributes.metadata.metadescription}
          nextSeoProps={{
            nofollow: !attributes.indexable,
            noindex: !attributes.indexable,
          }}
          canonical={canonical}
        />
      )}
      <div data-testid="stories-page">
        <HeroHeader />

        <SectionShape color={COLORS.SECONDARY_900} height={80} className="hide-xs" />

        <S.StoriesCardsWrapper className="relative pb-16">
          {media_legacy.gtSm && (
            <section className="container-lg pb-16">
              <StoryCard linkStories={newStory} isNewStory={true} isHeroCard={true} />
            </section>
          )}

          {optionCountry && optionsType && (
            <StoriesSorter
              optionsType={optionsType}
              optionCountry={optionCountry}
              selectedType={selectedType}
              selectedCountry={selectedCountry}
              setSelectedType={setSelectedType as Dispatch<SetStateAction<StoriesTypeProp>>}
              setSelectedCountry={setSelectedCountry as Dispatch<SetStateAction<StoriesTypeProp>>}
            />
          )}
          <section className="container-lg px-0 layout-row layout-wrap layout-align-start-start">
            {sortedStories && sortedStories?.length > 0 ? (
              sortedStories.map((story, index) => {
                return (
                  <div key={`${story}-${index}`} className="flex-100 flex-sm-50 flex-gt-sm-33 p-4 layout-column">
                    <StoryCard
                      className="flex-1"
                      linkStories={story}
                      isNewStory={media_legacy.gtSm ? false : index === 0}
                      isHeroCard={false}
                      link={story.url}
                    />
                  </div>
                );
              })
            ) : (
              <Fragment></Fragment>
            )}
          </section>
        </S.StoriesCardsWrapper>
        <PressSection
          mode="dark"
          title={
            <Trans i18nKey={'stories:press.title'}>
              People talk about us in the <b>press</b>
            </Trans>
          }
        />
        <section className="py-16 container-lg">
          <AgencySearchBlock trackingPage={'stories'} className="pb-3 " />
        </section>
      </div>
    </Fragment>
  );
};

export default StoriesPage;
